<script setup lang="ts">
import MarkdownIt from 'markdown-it';
import type { InterestRung } from '@@/types/api';
import { useInformationalEventsApi } from '~/composables/api/use-informational-events-api';
import { useAuthStore } from '~/stores/use-auth-store';
import { useMyInterests } from '~/stores/use-my-interests';
import { useInterestActions } from '~/composables/use-interest-actions';
import { useInterestResume } from '~/composables/use-interest-resume';
import { almanacInterestControlFor } from '~/utils/event-policy';
import {
  stripMarkdownToPlain,
  truncateAtWord,
} from '~/utils/sanitize-meta-content';

definePageMeta({
  validate: (route) => isCanonicalUuid(String(route.params.id)),
});

const config = useRuntimeConfig();
const route = useRoute();
const authStore = useAuthStore();
const requestOrigin = config.public.siteUrl || useRequestURL().origin;
const id = computed(() => String(route.params.id));

const {
  data,
  status,
  error: asyncError,
  refresh,
} = useAsyncData(
  `informational-event-${id.value}`,
  async () => {
    try {
      return await useInformationalEventsApi().get(id.value);
    } catch (err: unknown) {
      const errStatus =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status: number }).status)
          : 500;
      throw createError({
        statusCode: errStatus === 404 ? 404 : 500,
        statusMessage: errStatus === 404 ? 'Event not found.' : undefined,
      });
    }
  },
  { server: !config.public.mock },
);

// In client-only mode, useAsyncData captures errors in its error ref
// rather than triggering the error page. Show the error page explicitly.
watch(asyncError, (err) => {
  if (err) showError(err);
});

const event = computed(() => data.value ?? null);

// --- Interest (non-transactional engagement) --------------------------------
// Almanac listings carry NO `my_rung`; the viewer's declared rung comes from the
// shared interest store. This is an engagement affordance only — it adds no
// transactional (CTA / commit / purchase / roster) surface to the read-only
// informational page.
const myInterests = useMyInterests();

// Warm the shared store on the client (the store's `ensureUpcoming` is
// authed-only + client-only internally, so this is inert otherwise).
onMounted(() => {
  myInterests.ensureUpcoming();
});

// The store owns the authoritative rung. Seed/sync a local ref from it so the
// control reflects both the loaded value and optimistic `localSet` upserts.
const myRung = ref<InterestRung | null>(null);
watch(
  () => myInterests.rungByEventId.get(id.value) ?? null,
  (rung) => {
    myRung.value = rung;
  },
  { immediate: true },
);

// Writable count refs so the action composable can optimistically mutate and
// revert; re-synced from the public resource whenever it (re)loads.
const interestedCount = ref(event.value?.interested_count ?? 0);
const goingCount = ref(event.value?.going_count ?? 0);
watch(
  () => [event.value?.interested_count, event.value?.going_count] as const,
  ([interested, going]) => {
    interestedCount.value = interested ?? 0;
    goingCount.value = going ?? 0;
  },
);

// The backend already excludes truly-concluded listings; this is a defensive
// guard sharing the same pure helper the discover cards use. Coarse/undated
// timings are never treated as concluded (no datetime is fabricated).
const isConcluded = computed(() =>
  isInformationalEventConcluded(event.value?.timing),
);

const interestDescriptor = computed(() =>
  almanacInterestControlFor(
    isConcluded.value,
    authStore.isAuthenticated,
    myRung.value,
  ),
);

const {
  isPending: isInterestPending,
  snackbar: interestSnackbar,
  snackbarText: interestSnackbarText,
  snackbarColor: interestSnackbarColor,
  setInterest,
} = useInterestActions({
  source: 'almanac',
  eventId: id,
  myRung,
  interestedCount,
  goingCount,
  engagementMeta: () => ({
    title: event.value?.title,
    kind: event.value?.event_kind,
    timing_display: event.value?.timing?.display ?? null,
  }),
  // Re-fetch the public resource to true up authoritative counts after a toggle.
  refresh: async () => {
    await refresh();
  },
});

// Anti-flash / anti-stale gate: an authed viewer briefly renders as "not
// interested" between the anon SSR paint and the store load. Keep the control
// mounted but inert until the store has loaded the real rung. (Anon viewers
// need no gate — their action is `login`.)
const interestPending = computed(
  () =>
    isInterestPending.value ||
    (authStore.isAuthenticated && !myInterests.loadedUpcoming),
);

// Replay an intended rung after an anon → sign-in round-trip (?interest=...),
// once the store has loaded and the control is a live toggle. One-shot.
useInterestResume({
  ready: computed(
    () =>
      interestDescriptor.value.action === 'toggle' &&
      myInterests.loadedUpcoming,
  ),
  resume: (rung) => setInterest(rung),
});

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const renderedDescription = computed(() =>
  md.render(event.value?.description ?? ''),
);

const hasDescription = computed(
  () => (event.value?.description ?? '').trim().length > 0,
);

// Two distinct external affordances, both now live on staging via arvee-backend
// #1626: `source_url` is backfilled for ~all events (provenance / official page),
// while `links.register` maps Almanac's `registration_url` and carries for ~19%
// (registration / tickets). They are deliberately separate — each affordance
// renders only when its own URL resolves.
const officialUrl = computed(
  () =>
    event.value?.links?.official ??
    event.value?.source_url ??
    event.value?.links?.source ??
    null,
);

const registerUrl = computed(
  () => event.value?.links?.register ?? event.value?.links?.tickets ?? null,
);

const hasAnyLink = computed(
  () => officialUrl.value != null || registerUrl.value != null,
);

// Anonymous visitors are prompted to sign in before following the source link;
// authenticated Arvee users get the external link directly. SSR renders the
// sign-in branch (always unauthenticated server-side); it flips reactively to
// the external link after auth.client.ts hydrates — the same mechanism AppHeader
// uses. This only toggles visibility, so no isLoading gate is needed.
const loginUrl = computed(
  () => `/login?redirect_to=${encodeURIComponent(route.fullPath)}`,
);

const venue = computed(() => event.value?.venue ?? null);

const venueLocality = computed(() => {
  const v = venue.value;
  if (!v) return '';
  return [v.city, v.state].filter(Boolean).join(', ');
});

const organizerName = computed(
  () => event.value?.organizer?.display_name ?? null,
);

const organizerUrl = computed(() => event.value?.organizer?.url ?? null);

const pageTitle = computed(() =>
  event.value
    ? `${event.value.title} | Arvee Events`
    : 'Discover | Arvee Events',
);

const pageDescription = computed(() =>
  event.value
    ? truncateAtWord(stripMarkdownToPlain(event.value.description ?? ''), 160)
    : '',
);

const canonicalUrl = computed(() =>
  event.value ? `${requestOrigin}/discover/${event.value.id}` : '',
);

useHead(
  () => ({
    title: pageTitle.value,
    meta: [
      { name: 'description', content: pageDescription.value },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: pageDescription.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl.value },
    ],
    link: canonicalUrl.value
      ? [{ rel: 'canonical', href: canonicalUrl.value }]
      : [],
  }),
  { tagPriority: 10 },
);
</script>

<template>
  <div>
    <!-- Loading State -->
    <template v-if="status === 'pending' || status === 'idle'">
      <v-skeleton-loader
        type="image"
        class="mb-6 mx-auto"
        style="max-width: 800px"
      />
      <v-skeleton-loader type="heading" class="mb-4" />
      <v-skeleton-loader type="paragraph" class="mb-4" />
      <v-skeleton-loader type="paragraph" />
    </template>

    <!-- Content -->
    <template v-else-if="event">
      <v-btn
        variant="text"
        prepend-icon="mdi-arrow-left"
        to="/browse"
        class="mb-4"
      >
        Back to Discover
      </v-btn>

      <!-- Hero -->
      <DiscoverInformationalEventCover
        :id="event.id"
        :title="event.title"
        :event-kind="event.event_kind"
        :city="event.venue?.city"
        :state="event.venue?.state"
        class="mx-auto mb-6"
        style="max-width: 800px"
      />

      <!-- Header -->
      <div class="d-flex align-center flex-wrap ga-3 mb-4">
        <h1 class="text-h4">{{ event.title }}</h1>
        <DiscoverThirdPartyBadge />
      </div>

      <!-- Timing (rendered verbatim from the backend) -->
      <div class="d-flex align-start mb-3">
        <v-icon class="mr-3 mt-1" color="primary">mdi-calendar</v-icon>
        <div class="text-body-1 font-weight-medium">
          {{ event.timing.display }}
        </div>
      </div>

      <!-- Venue (text only, no map) -->
      <div v-if="venue" class="d-flex align-start mb-3">
        <v-icon class="mr-3 mt-1" color="primary">mdi-map-marker</v-icon>
        <div>
          <div v-if="venue.name" class="text-body-1 font-weight-medium">
            {{ venue.name }}
          </div>
          <div v-if="venueLocality" class="text-body-2 text-medium-emphasis">
            {{ venueLocality }}
          </div>
          <div v-if="venue.address" class="text-body-2 text-medium-emphasis">
            {{ venue.address }}
          </div>
        </div>
      </div>

      <!-- Organizer -->
      <div v-if="organizerName" class="d-flex align-start mb-6">
        <v-icon class="mr-3 mt-1" color="primary">mdi-account</v-icon>
        <div class="text-body-1 font-weight-medium">
          Organized by
          <a
            v-if="organizerUrl"
            :href="organizerUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ organizerName }}
          </a>
          <template v-else>{{ organizerName }}</template>
        </div>
      </div>

      <!-- Description -->
      <div v-if="hasDescription" class="mb-6">
        <h2 class="text-h6 mb-3">About this event</h2>
        <!-- eslint-disable vue/no-v-html — markdown-it with html:false; no raw user HTML rendered -->
        <div
          class="event-description text-body-1"
          v-html="renderedDescription"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <!-- Interest (non-transactional engagement): social-proof counts plus a
           deselectable Interested / Going toggle. This is NOT a CTA / commit /
           purchase / roster control — the listing stays informational-only. -->
      <div class="mb-6">
        <EventsSocialProofCounts
          :interested-count="interestedCount"
          :going-count="goingCount"
          class="mb-2"
        />
        <EventsEventInterestControl
          v-if="interestDescriptor.visible"
          :descriptor="interestDescriptor"
          :event-id="id"
          :pending="interestPending"
          @select="setInterest"
        />
      </div>

      <!-- External affordances: registration (primary) and the official /
           provenance page (secondary), each rendered only when its URL
           resolves. Gated — authenticated users get the external links;
           anonymous visitors get a single sign-in prompt. Nothing renders when
           the event carries no links at all. -->
      <template v-if="hasAnyLink">
        <div
          v-if="authStore.isAuthenticated"
          class="d-flex flex-wrap ga-3 mb-6"
        >
          <!-- Register: primary CTA, only when present -->
          <v-btn
            v-if="registerUrl"
            color="primary"
            variant="flat"
            append-icon="mdi-open-in-new"
            :href="registerUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Register
          </v-btn>
          <!-- Official page: secondary, only when present -->
          <v-btn
            v-if="officialUrl"
            color="primary"
            variant="outlined"
            append-icon="mdi-open-in-new"
            :href="officialUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            View official page
          </v-btn>
        </div>
        <!-- Anonymous: a single sign-in prompt covering whichever links exist -->
        <v-btn
          v-else
          color="primary"
          variant="flat"
          prepend-icon="mdi-login"
          :to="loginUrl"
          class="mb-6"
        >
          Sign in to view event links
        </v-btn>
      </template>

      <!-- Disclaimer -->
      <p class="text-body-2 text-medium-emphasis mb-6">
        This listing is sourced from a third party and is provided for
        informational purposes only. It is not a joinable Arvee event.
      </p>

      <!-- Community submission prompt -->
      <v-divider class="mb-6" />
      <div class="d-flex flex-wrap align-center ga-3">
        <div class="text-body-2 text-medium-emphasis">
          Know an event we're missing? Help grow the Almanac.
        </div>
        <v-spacer />
        <DiscoverSubmitEventCta variant="tonal" prepend-icon="mdi-plus-box" />
      </div>

      <v-snackbar
        v-model="interestSnackbar"
        :color="interestSnackbarColor"
        :timeout="4000"
      >
        {{ interestSnackbarText }}
      </v-snackbar>
    </template>
  </div>
</template>

<style scoped>
.event-description :deep(h1),
.event-description :deep(h2),
.event-description :deep(h3) {
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.event-description :deep(ul),
.event-description :deep(ol) {
  padding-left: 1.5em;
  margin-bottom: 1em;
}

.event-description :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.event-description :deep(code) {
  background: rgb(var(--v-theme-surface-variant));
  padding: 0.15em 0.4em;
  border-radius: 4px;
  font-size: 0.9em;
}

.event-description :deep(pre) {
  background: rgb(var(--v-theme-surface-variant));
  padding: 1em;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 1em;
}

.event-description :deep(blockquote) {
  border-left: 3px solid rgb(var(--v-theme-outline));
  padding-left: 1em;
  margin-left: 0;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.event-description :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
